import { OtsDateTime } from '../../components/OtsDateTime';
import { useState } from 'react';
import type { RrcLocationProfile } from '@ots-frontend/ots-data';
import {
  OtsDataTable,
  OtsDataTableHead,
  OtsDataTableBody,
  OtsDataTableRow,
  OtsDataTableHeaderCell,
  OtsDataTableCell,
  OtsDataTableSortableHeaderCell,
} from '../../components/OtsDataTable';
import {
  OtsDataTableLink,
  OtsDataTableEmpty,
} from '../../components/OtsDataTableCells';
import { OtsTablePanel } from '../../components/OtsTablePanel';
import { OtsSearchInput } from '../../components/OtsSearchInput';
import { OtsActionButton } from '../../components/OtsButton';
import { OtsSwitch } from '../../components/OtsSwitch';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { ComplianceStatusBadge } from '../ComplianceStatusBadge';
import { locationProfileCodeLabel } from './rrc-location-profile-ui';

export type RrcLocationProfilesViewProps = {
  profiles: RrcLocationProfile[];
  loading?: boolean;
  error?: string;
  compact?: boolean;
  fill?: boolean;
  includeHistory?: boolean;
  onIncludeHistory?: (value: boolean) => void;
  onOpen: (profile: RrcLocationProfile) => void;
  onNew?: () => void;
  onRefresh?: () => void;
};
export const RrcLocationProfilesView = ({
  profiles,
  loading,
  error,
  compact,
  fill = false,
  includeHistory,
  onIncludeHistory,
  onOpen,
  onNew,
  onRefresh,
}: RrcLocationProfilesViewProps) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [ascending, setAscending] = useState(true);
  const filtered = profiles
    .filter((profile) =>
      [
        profile.facilityName,
        profile.locationName,
        profile.leaseNumber,
        profile.facilityPermitNumber,
        profile.locationType,
        locationProfileCodeLabel(profile.rrcDistrict),
        profile.status,
      ].some((value) =>
        value?.toLowerCase().includes(search.toLowerCase().trim())
      )
    )
    .sort(
      (a, b) =>
        (ascending ? 1 : -1) *
          (a.facilityName ?? '').localeCompare(b.facilityName ?? '') ||
        b.version - a.version
    );
  const pageSize = 15;
  const currentPage = Math.min(
    page,
    Math.max(1, Math.ceil(filtered.length / pageSize))
  );
  return (
    <OtsTablePanel
      title="RRC Location Profiles"
      compact={compact}
      fill={fill}
      description={`${filtered.length} profiles`}
      actions={
        <>
          {onRefresh && (
            <OtsActionButton
              size="compact"
              onClick={onRefresh}
              disabled={loading}
            >
              Refresh
            </OtsActionButton>
          )}
          {onNew && (
            <OtsActionButton variant="primary" size="compact" onClick={onNew}>
              New location profile
            </OtsActionButton>
          )}
        </>
      }
      toolbar={
        <>
          <OtsSearchInput
            label="Search location profiles"
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setPage(1);
            }}
            onClear={() => {
              setSearch('');
              setPage(1);
            }}
            size={compact ? 'compact' : 'default'}
          />
          {onIncludeHistory && (
            <OtsSwitch
              label="Include previous versions"
              checked={includeHistory ?? false}
              onCheckedChange={(value) => {
                setPage(1);
                onIncludeHistory(value);
              }}
              size={compact ? 'compact' : 'default'}
            />
          )}
        </>
      }
      pagination={{
        page: currentPage,
        pageSize,
        totalItems: filtered.length,
        onPageChange: setPage,
        itemLabel: 'profiles',
        disabled: loading,
      }}
    >
      {loading ? (
        <OtsDataTableEmpty loading loadingText="Loading location profiles…">
          {null}
        </OtsDataTableEmpty>
      ) : error ? (
        <div className="p-4">
          <OtsInlineAlert
            tone="danger"
            title="Location profiles unavailable"
            action={
              onRefresh && (
                <OtsActionButton size="compact" onClick={onRefresh}>
                  Try again
                </OtsActionButton>
              )
            }
          >
            {error}
          </OtsInlineAlert>
        </div>
      ) : !filtered.length ? (
        <OtsDataTableEmpty compact={compact}>
          {search
            ? 'No location profiles match your search.'
            : 'No location profiles have been configured.'}
        </OtsDataTableEmpty>
      ) : (
        <OtsDataTable
          aria-label="RRC location profiles"
          density={compact ? 'compact' : 'default'}
          contained={false}
        >
          <OtsDataTableHead>
            <OtsDataTableRow>
              <OtsDataTableSortableHeaderCell
                direction={ascending ? 'asc' : 'desc'}
                onSort={() => setAscending(!ascending)}
              >
                Facility
              </OtsDataTableSortableHeaderCell>
              {[
                'Type',
                'District',
                'Effective start',
                'Effective end',
                'Status',
                'Version',
              ].map((label) => (
                <OtsDataTableHeaderCell key={label}>
                  {label}
                </OtsDataTableHeaderCell>
              ))}
            </OtsDataTableRow>
          </OtsDataTableHead>
          <OtsDataTableBody>
            {filtered
              .slice((currentPage - 1) * pageSize, currentPage * pageSize)
              .map((profile) => (
                <OtsDataTableRow key={profile.id}>
                  <OtsDataTableCell>
                    <OtsDataTableLink
                      onClick={() => onOpen(profile)}
                      ariaLabel={`Open ${
                        profile.facilityName ??
                        profile.locationName ??
                        'location profile'
                      }, version ${profile.version}`}
                    >
                      {profile.facilityName ??
                        profile.locationName ??
                        'Unnamed facility'}
                    </OtsDataTableLink>
                  </OtsDataTableCell>
                  <OtsDataTableCell>{profile.locationType}</OtsDataTableCell>
                  <OtsDataTableCell>
                    {locationProfileCodeLabel(profile.rrcDistrict)}
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    <OtsDateTime
                      value={profile.effectiveStartDttm}
                      empty="Not set"
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    <OtsDateTime
                      value={profile.effectiveEndDttm}
                      empty="No end date"
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    <ComplianceStatusBadge
                      status={profile.status}
                      compact={compact}
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell>{profile.version}</OtsDataTableCell>
                </OtsDataTableRow>
              ))}
          </OtsDataTableBody>
        </OtsDataTable>
      )}
    </OtsTablePanel>
  );
};
